import InteractiveSection from '../components/InteractiveSection'
import Menu from '../components/Menu'
import ObservedScrolling from '../components/ObservedScrolling'
import RisingNumber from '../components/RisingNumber'
import { isMobile, scrollTo, scrollToElement } from '../lib/helpers'
import { easing, EaseCurve, EaseType } from '../lib/easing'
import Interval from '../lib/Interval'
import { clamp } from '../lib/math'

addEventListener('DOMContentLoaded', () => {
	if (document.body.classList.contains('home')) {
		initializeCursorEffects()
		initializeDepthEffects()
		initializeScrollEffects()
		projects.init()

		if (isMobile()) {
			document.body.classList.add('mobile')
		}

		if (navigator.maxTouchPoints > 1 || /(Apple)/gi.test(navigator.vendor)) {
			document.documentElement.setAttribute('data-normal-scroll', 'true')
		}
	}
})

function initializeCursorEffects() {
	const cursor = document.getElementById('cursor') as HTMLDivElement
	if (!cursor) {
		return
	}

	let activeElement: HTMLElement

	document.querySelectorAll('[data-cursor]')
		.forEach((element: HTMLElement) => {
			element.addEventListener('mousemove', (event) => {
				if (activeElement == element) {
					cursor.className = 'visible ' + (element.dataset.cursor || '')
				}
			})

			element.addEventListener('mouseenter', () => {
				activeElement = element
			})

			element.addEventListener('mouseleave', () => {
				if (activeElement === element) {
					activeElement = null
				}
				cursor.classList.remove('visible')
			})
		})

	let mouse = { x: 0, y: 0 }
	let last = { ...mouse }
	let current = { ...last }
	let lastTimestamp = 0
	let animationPosition = 1

	const tick = (timestamp: number = 0) => {
		requestAnimationFrame(tick)

		const delta = timestamp - lastTimestamp
		lastTimestamp = timestamp
		animationPosition = Math.min(animationPosition + (delta / 500), 1)
		const ease = easing(animationPosition, EaseType.Out, EaseCurve.Quint)

		const xDelta = mouse.x - last.x
		const yDelta = mouse.y - last.y
		current.x = last.x + xDelta * ease
		current.y = last.y + yDelta * ease

		cursor.style.left = current.x + 'px'
		cursor.style.top = current.y + 'px'
	}

	addEventListener('mousemove', (event) => {
		last = { ...current }
		mouse.x = event.clientX
		mouse.y = event.clientY
		animationPosition = 0
	})
//
	tick()
}

function initializeDepthEffects() {
	const elements = Array.from(document.querySelectorAll('.depth-effect')) as HTMLElement[]

	let activeElement: HTMLElement
	let pointer = { x: 0, y: 0 }

	for (const element of elements) {
		element.addEventListener('pointermove', (event) => {
			pointer.x = event.offsetX
			pointer.y = event.offsetY
		})

		element.addEventListener('pointerenter', (event) => {
			activeElement = element
		})

		element.addEventListener('mouseleave', () => {
			if (activeElement == element) {
				activeElement = null
			}
		})
	}

	const tick = (timestamp: number = 0) => {
		requestAnimationFrame(tick)

		if (activeElement) {
			const offsetX = Math.cos(timestamp * 0.0008) * 0.3
			const offsetY = Math.sin(timestamp * 0.0008) * 0.3
			const x = (pointer.x / activeElement.offsetWidth) * 2 - 1 + offsetX
			const y = (pointer.y / activeElement.offsetHeight) * 2 - 1 + offsetY
			activeElement.style.setProperty('--pointer-x', `${x}`)
			activeElement.style.setProperty('--pointer-y', `${y}`)
		}
	}

	tick()
}

function initializeScrollEffects() {
	const scroll = new ObservedScrolling({
		modifyingElement: Array.from(document.querySelectorAll('.scroll-follower'))
	})

	//
	// On scroll effects.
	const heroElement = document.getElementById('hero') as HTMLDivElement
	const backgroundGraphic = document.querySelector('#background-graphic-holder div') as HTMLElement
	const contentSections = Array.from(document.querySelectorAll('#content-holder section')) as HTMLElement[]
	const ceoPhoto = document.querySelector('figure.ruud') as HTMLElement
	let currentSection: HTMLElement

	const onScroll = () => {
		const mobile = isMobile()

		// Fixed header.
		const y = scrollY || window.pageYOffset
		const header = document.querySelector('header')
		if (header) {
			const headerHolder = header.parentElement
			const fixedHeader = headerHolder.getBoundingClientRect().top <= 0
			document.body.classList.toggle('fixed-header', fixedHeader)
		}

		if (y >= 200 && !document.body.classList.contains('scrolled')) {
			document.body.classList.add('scrolled')
		}

		document.body.classList.toggle('scrolled-mobile', y >= innerHeight / 2)

		// Set the color scheme of the header, based on the current visible section.
		// Find the closest section that's *above* the current scroll position.
		const offset = isMobile() ? header.offsetHeight : header.offsetHeight / 2
		const current = contentSections.reduce((prev, el) => {
			const elTop = el.getBoundingClientRect().top
			if (elTop > offset) {
				return prev
			}
			else if (prev) {
				const prevTop = prev.getBoundingClientRect().top
				return prevTop < elTop ? el : prev
			}
			else {
				return el
			}
		}, null as HTMLElement)

		if (current != currentSection) {
			for (const contentSection of contentSections) {
				if (contentSection.dataset.headerStyle) {
					header.classList.remove(...contentSection.dataset.headerStyle.split(' '))
				}
			}

			if (current && current.dataset.headerStyle) {
				header.classList.add(...current.dataset.headerStyle.split(' '))
			}

			Menu.setActiveSection(contentSections.indexOf(current))

			currentSection = current
		}

		// Hero.
		heroElement.style.setProperty('--hero-image-y-translate', `${y * 0.5}px`)

		// Photo of the CEO.
		let ceoOffset = y - innerHeight // Sneaky way to get the hero height.
		ceoOffset = mobile ? 1 : ceoOffset * 0.2
		ceoPhoto.style.transform = `translateY(${ceoOffset}px)`

		// Background scaling.
		const totalSections = 5
		const strength = 0.15
		let scale = 1 + (clamp(y / (innerHeight * totalSections), 0, 1) * strength)
		scale = mobile ? 1 : scale
		backgroundGraphic.style.transform = `scale(${scale})`
	}

	addEventListener('scroll', onScroll, { passive: true })
	addEventListener('DOMContentLoaded', onScroll)
	addEventListener('load', onScroll)

	//
	// Interactive sections.
	const numberSections = Array.from(document.querySelectorAll('.numbers-section')) as HTMLElement[]
	for (const numberSection of numberSections) {
		const section = new InteractiveSection(numberSection)
		const numbers = new RisingNumber(numberSection)

		section.becameVisible = () => {
			InteractiveSection.prototype.becameVisible.call(section)
			numbers.startAnimation()
		}
	}

	// Misc sections,
	new InteractiveSection(document.querySelector('#message-ceo'))
	new InteractiveSection(document.querySelector('#chapters'))
	new InteractiveSection(document.querySelector('#final'))
}

// Projects section.
// TODO: Better separate the mobile and desktop versions.
const projects = new class Projects {
	// Values for the animation.
	private animationPosition = 0
	private startPosition = 0
	private currentProject = 0
	private readonly interval: Interval

	// Interactive sections.
	private section: InteractiveSection
	private backgroundSection: InteractiveSection

	// Misc elements.
	private backgroundsHolder: HTMLDivElement
	private backgrounds: HTMLElement[]
	private details: HTMLDivElement[]
	private infoContainers: HTMLDivElement[]
	private thumbnails: HTMLAnchorElement[]

	public isOpen = false

	// Convenient getters.
	public get current(): number {
		return this.currentProject < 0
			? (this.thumbnails.length + (this.currentProject % this.thumbnails.length)) % this.thumbnails.length
			: this.currentProject % this.thumbnails.length
	}

	private get page(): number {
		return Math.floor(this.currentProject / this.projectsCount)
	}

	public get projectsCount(): number {
		return this.thumbnails.length ?? 0
	}

	public constructor() {
		this.interval = new Interval({
			autostart: false,
			duration: 1e3,
			loop: false,
		}, this.tick)
	}

	public init() {
		// Sections. Binding key press event when visible, unbinding when becomign invisible.
		this.backgroundSection = new InteractiveSection(document.querySelector('#projects-background'))
		this.section = new class ProjectsSection extends InteractiveSection {
			constructor() {
				super(document.querySelector('#projects'), {
					keepActive: true
				})
			}
		}

		// All elements.
		this.backgroundsHolder = document.querySelector('#projects-background .right')
		this.backgrounds = Array.from(this.backgroundsHolder.querySelectorAll('.project'))
		this.details = Array.from(document.querySelectorAll('#projects .projects-details .project'))
		this.infoContainers = this.details.map(el => el.querySelector('.info'))
		this.thumbnails = Array.from(document.querySelectorAll('#projects .projects-thumbnails-holder a'))
		const header = document.querySelector('header')
		const detailsContainer = this.details[0].parentElement.parentElement

		for (const [index, thumbnail] of this.thumbnails.entries()) {
			thumbnail.addEventListener('click', (event) => {
				event.preventDefault()
				const offset = index < this.current ? index + this.projectsCount : index
				this.selectProject((this.page * this.projectsCount) + offset)

				if (isMobile()) {
					// ...
				}
			})
		}

		// Openening / closing a project.
		for (const [index, element] of this.details.entries()) {
			for (const el of Array.from(element.querySelectorAll('.info, .details'))) {
				el.addEventListener('click', (event) => {
					if (event.target instanceof HTMLAnchorElement) {
						return
					}

					event.preventDefault()
					this.isOpen = !this.isOpen
					document.body.classList.toggle('projects-open', this.isOpen)

					for (const [index, detail] of this.details.entries()) {
						if (window.innerWidth > 1024) {
							detail.classList.toggle('active', detail === element)
						}
						else {
							detail === element ? detail.classList.toggle('active') : null
						}

						// Changing the value for the cursor between `read` and `back`.
						this.infoContainers[index].dataset.cursor = this.isOpen ? 'close' : 'read'
					}

					if (isMobile()) {
						const topOffset = detailsContainer.getBoundingClientRect().top + scrollY - header.offsetHeight
						const projectHeight = detailsContainer.offsetHeight / this.details.length
						const y = topOffset + (projectHeight * index)
						scrollTo(0, y)
					}
					else {
						scrollToElement(this.section.element)
					}
				})
			}
		}

		this.tick(1)
		addEventListener('resize', () => this.tick(1))

		//
		// Mobile effects et al.
		//
		// Scrolling effect for the mobile variant.
		addEventListener('scroll', () => {
			if (!isMobile()) {
				return
			}

			const top = -detailsContainer.getBoundingClientRect().top + header.offsetHeight - 1
			const y = top / (detailsContainer.offsetHeight - innerHeight)
			const progress = clamp(y, 0, 1)

			for (const [index, project] of this.details.entries()) {
				const offset = index - (progress * (this.details.length - 1))

				if (offset < 0) {
					project.style.transform = `translateY(${offset * 100}%)`
					project.style.setProperty('--offset', '0')
				}
				else {
					const o = Math.log(offset + 1)
					const scale = 1 - (o * 0.2)
					project.style.transform = `translateY(${o}%) scale(${scale})`
					project.style.setProperty('--offset', (offset / this.details.length).toFixed(5))
				}
			}
		}, { passive: true })

		if (isMobile()) {
			for (const detail of this.details) {
				detail.classList.remove('active')
			}
		}
	}

	private tick = (pos: number) => {
		// Desktop animation.
		if (!isMobile()) {
			const width = this.thumbnails[0].offsetWidth
			const gap = innerHeight * 0.06

			const ease = easing(pos, EaseType.Out, EaseCurve.Quint)
			const distance = this.currentProject - this.startPosition
			this.animationPosition = this.startPosition + (distance * ease)

			for (const [index, thumbnail] of this.thumbnails.entries()) {
				let offset = (index - this.animationPosition) % this.projectsCount
				if (offset < 0) {
					offset = this.projectsCount + offset
				}

				// Different offsets for desktop vs mobile.
				const x = offset % this.projectsCount * (width + gap)
				// thumbnail.style.transform = `translateX(${-x}px)`
			}
		}
	}

	public selectProject(next: number) {
		if (next != this.currentProject) {
			this.interval.stop()

			const direction = Math.sign(this.currentProject - next)
			this.startPosition = isMobile() ? scrollY : this.animationPosition
			this.currentProject = next

			for (const [index, background] of this.backgrounds.entries()) {
				background.classList.toggle('active', index == this.current)
				background.classList.toggle('in-right', index == this.current && direction != -1)
				background.classList.toggle('in-left', index == this.current && direction == -1)
				this.details[index].classList.toggle('active', index == this.current)
				this.thumbnails[index].classList.toggle('active', index == this.current)
			}

			this.interval.start()
		}
	}

	public close() {
		this.isOpen = false
		document.body.classList.remove('projects-open')

		for (const [index, detail] of this.details.entries()) {
			detail.classList.remove('active')
			this.infoContainers[index].dataset.cursor = this.isOpen ? 'close' : 'read'
		}
	}
}